import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FaComments } from 'react-icons/fa';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { useAlert } from '../../../../contexts/AlertContext';
import EmptyState from '../../../common/EmptyState';
import ConfirmationModal from '../../../common/ConfirmationModal';
import TalkingPointStatusSelector from '../../../common/TalkingPointStatusSelector';
import { TALKING_POINT_STATUSES } from '../../../../constants/talkingPointStatuses';
import ActionItemCard, {
  ACTION_ITEM_STATUSES,
  ACTION_ITEM_PRIORITIES,
} from './ActionItemCard';
import ActionItemForm from './ActionItemForm';
import NoteCard from './NoteCard';
import NoteForm from './NoteForm';
import DiscussionLeftPanel from './DiscussionLeftPanel';
import LinkGoalActionModal from './LinkGoalActionModal';
import LinkFeedbackResultsModal from './LinkFeedbackResultsModal';
import LinkItemsMenu from './LinkItemsMenu';
import CreateIDPModal from './CreateIDPModal';
import IDPViewModal from './IDPViewModal';
import FeedbackViewModal from './FeedbackViewModal';
import LinkedItemsSection from './LinkedItemsSection';
import { MEETING_REFERENCE_TYPES } from '../../../../constants/meetingReferenceTypes';
import { MEETING_ATTENDEE_TYPES } from '../../../../constants/meetingAttendeeTypes';
import { MEETING_NOTE_TYPES } from '../../../../constants/meetingNoteTypes';
import { IDP_TABS } from '../../../IndividualDevelopmentPlans/components/IDPForm';

// View mode constants (component-specific, not shared)
export const VIEW_MODES = {
  TALKING_POINT: 'talking-point',
  GENERAL_NOTES: 'general-notes',
};

const DiscussionTab = ({ meetingId, meeting, permissions, onRefresh, onChange }) => {
  const { showAlert } = useAlert();
  const [talkingPoints, setTalkingPoints] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [generalNotes, setGeneralNotes] = useState([]);
  const [viewMode, setViewMode] = useState(null);

  // Action Items state
  const [actionItems, setActionItems] = useState([]);
  const [expandedActionItems, setExpandedActionItems] = useState(new Set());
  const [editingActionItemId, setEditingActionItemId] = useState(null);
  const [editingActionItem, setEditingActionItem] = useState({});
  const [showAddActionItemForm, setShowAddActionItemForm] = useState(false);
  const [newActionItem, setNewActionItem] = useState({
    title: '',
    description: '',
    ownerEmployeeId: null,
    ownerEmployee: null,
    priority: ACTION_ITEM_PRIORITIES.MEDIUM,
    status: ACTION_ITEM_STATUSES.OPEN,
    deadlineAt: null,
  });
  const [isAddingActionItem, setIsAddingActionItem] = useState(false);
  const [actionItemToDelete, setActionItemToDelete] = useState(null);
  const [actionItemSourceNote, setActionItemSourceNote] = useState(null); // For creating action from note
  const [noteMenuAnchor, setNoteMenuAnchor] = useState(null);
  const [selectedNoteForMenu, setSelectedNoteForMenu] = useState(null);

  // Meeting Notes contextual menu state
  const [meetingNotesMenuAnchor, setMeetingNotesMenuAnchor] = useState(null);
  const [linkGoalModalOpen, setLinkGoalModalOpen] = useState(false);
  const [linkedReferences, setLinkedReferences] = useState([]);
  const [linkedRefToDelete, setLinkedRefToDelete] = useState(null);

  // IDP View Modal state
  const [idpViewModal, setIdpViewModal] = useState({ open: false, goalId: null, initialTab: IDP_TABS.GENERAL });

  // Feedback linking state
  const [linkFeedbackModalOpen, setLinkFeedbackModalOpen] = useState(false);
  const [feedbackViewModal, setFeedbackViewModal] = useState({ open: false, assignmentId: null });

  // Talking Point linking state
  const [talkingPointReferences, setTalkingPointReferences] = useState({});
  const [talkingPointMenuAnchor, setTalkingPointMenuAnchor] = useState(null);
  const [talkingPointLinkGoalModalOpen, setTalkingPointLinkGoalModalOpen] = useState(false);
  const [talkingPointLinkFeedbackModalOpen, setTalkingPointLinkFeedbackModalOpen] = useState(false);
  const [tpLinkedRefToDelete, setTpLinkedRefToDelete] = useState(null);

  // Create IDP modal state
  const [createIdpModalOpen, setCreateIdpModalOpen] = useState(false);
  const [tpCreateIdpModalOpen, setTpCreateIdpModalOpen] = useState(false);

  useEffect(() => {
    fetchTalkingPoints();
    fetchGeneralNotes();
    fetchActionItems();
    fetchLinkedReferences();
  }, [meetingId]);

  const fetchLinkedReferences = async () => {
    try {
      const refs = await meetingsApi.getMeetingReferences(meetingId);
      // Filter for IDP_GOAL, IDP_ACTION, and PERFORMANCE_FEEDBACK_RESULT types
      setLinkedReferences((refs || []).filter(r =>
        r.refType === MEETING_REFERENCE_TYPES.IDP_GOAL || r.refType === MEETING_REFERENCE_TYPES.IDP_ACTION || r.refType === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT
      ));
    } catch (err) {
      console.error('Failed to fetch linked references:', err);
    }
  };

  const fetchTalkingPoints = async () => {
    try {
      setIsLoading(true);
      const points = await meetingsApi.getMeetingTalkingPoints(meetingId);
      setTalkingPoints(points || []);

      // Update selected point if it exists
      if (selectedPoint) {
        const updated = (points || []).find(p => p.id === selectedPoint.id);
        setSelectedPoint(updated || null);
      }
    } catch (err) {
      showAlert('Failed to load talking points', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGeneralNotes = async () => {
    try {
      const notes = await meetingsApi.getMeetingNotes(meetingId, { type: MEETING_NOTE_TYPES.GENERAL });
      setGeneralNotes(notes || []);
    } catch (err) {
      showAlert('Failed to load general notes', 'error', err);
    }
  };

  const fetchActionItems = async () => {
    try {
      const items = await meetingsApi.getActionItems(meetingId);
      setActionItems(items || []);
    } catch (err) {
      showAlert('Failed to load action items', 'error', err);
    }
  };

  const handleSelectPoint = (point) => {
    if (selectedPoint?.id === point.id && viewMode === VIEW_MODES.TALKING_POINT) {
      setSelectedPoint(null);
      setViewMode(null);
    } else {
      setSelectedPoint(point);
      setViewMode(VIEW_MODES.TALKING_POINT);
      // Fetch linked references for this talking point
      fetchTalkingPointReferences(point.id);
    }
    setNewNoteContent('');
    setShowAddNoteForm(false);
    // Reset action item form state when switching
    setShowAddActionItemForm(false);
    setNewActionItem(getDefaultActionItemState());
    setActionItemSourceNote(null);
    setEditingActionItemId(null);
    setEditingActionItem({});
  };

  const handleSelectGeneralNotes = () => {
    if (viewMode === VIEW_MODES.GENERAL_NOTES) {
      setViewMode(null);
    } else {
      setSelectedPoint(null);
      setViewMode(VIEW_MODES.GENERAL_NOTES);
    }
    setNewNoteContent('');
    setShowAddNoteForm(false);
    // Reset action item form state when switching
    setShowAddActionItemForm(false);
    setNewActionItem(getDefaultActionItemState());
    setActionItemSourceNote(null);
    setEditingActionItemId(null);
    setEditingActionItem({});
  };

  const handleStatusChange = async (point, newStatus) => {
    try {
      await meetingsApi.updateMeetingTalkingPoint(meetingId, point.id, {
        status: newStatus,
      });
      setTalkingPoints(prev =>
        prev.map(p => p.id === point.id ? { ...p, status: newStatus } : p)
      );
      if (selectedPoint?.id === point.id) {
        setSelectedPoint(prev => ({ ...prev, status: newStatus }));
      }
      onChange?.();
    } catch (err) {
      showAlert('Failed to update talking point status', 'error', err);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim() || !selectedPoint) return;

    try {
      setIsAddingNote(true);
      await meetingsApi.addMeetingNote(meetingId, {
        meetingTalkingPointId: selectedPoint.id,
        notes: newNoteContent.trim(),
      });
      showAlert('Note added', 'success');
      setNewNoteContent('');
      setShowAddNoteForm(false);
      onChange?.();
      fetchTalkingPoints();
    } catch (err) {
      showAlert('Failed to add note', 'error', err);
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleAddGeneralNote = async () => {
    if (!newNoteContent.trim()) return;

    try {
      setIsAddingNote(true);
      await meetingsApi.addMeetingNote(meetingId, {
        notes: newNoteContent.trim(),
      });
      showAlert('Note added', 'success');
      setNewNoteContent('');
      setShowAddNoteForm(false);
      onChange?.();
      fetchGeneralNotes();
    } catch (err) {
      showAlert('Failed to add note', 'error', err);
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;
    try {
      await meetingsApi.removeMeetingNote(meetingId, noteToDelete.id);
      showAlert('Note removed', 'success');
      setNoteToDelete(null);
      onChange?.();
      if (viewMode === VIEW_MODES.GENERAL_NOTES) {
        fetchGeneralNotes();
      } else {
        fetchTalkingPoints();
      }
    } catch (err) {
      showAlert('Failed to remove note', 'error', err);
    }
  };

  const toggleNoteExpanded = (noteId) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
        // Cancel editing if collapsing
        if (editingNoteId === noteId) {
          setEditingNoteId(null);
          setEditingNoteContent('');
        }
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.notes || note.content || '');
    // Make sure note is expanded
    setExpandedNotes(prev => new Set(prev).add(note.id));
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent('');
  };

  const handleSaveNote = async (noteId) => {
    if (!editingNoteContent.trim()) return;
    try {
      await meetingsApi.updateMeetingNote(meetingId, noteId, {
        notes: editingNoteContent.trim(),
      });
      showAlert('Note updated', 'success');
      setEditingNoteId(null);
      setEditingNoteContent('');
      onChange?.();
      if (viewMode === VIEW_MODES.GENERAL_NOTES) {
        fetchGeneralNotes();
      } else {
        fetchTalkingPoints();
      }
    } catch (err) {
      showAlert('Failed to update note', 'error', err);
    }
  };

  const handleAddNoteClick = () => {
    setShowAddNoteForm(true);
    setNewNoteContent('');
  };

  const handleCancelAddNote = () => {
    setShowAddNoteForm(false);
    setNewNoteContent('');
  };

  // Action Item Handlers
  const toggleActionItemExpanded = (actionItemId) => {
    setExpandedActionItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(actionItemId)) {
        newSet.delete(actionItemId);
        if (editingActionItemId === actionItemId) {
          setEditingActionItemId(null);
          setEditingActionItem({});
        }
      } else {
        newSet.add(actionItemId);
      }
      return newSet;
    });
  };

  const handleStartEditActionItem = (actionItem) => {
    setEditingActionItemId(actionItem.id);
    setEditingActionItem({
      title: actionItem.title || '',
      description: actionItem.description || '',
      ownerEmployeeId: actionItem.ownerEmployeeId || null,
      ownerEmployee: actionItem.ownerEmployee || null,
      priority: actionItem.priority || ACTION_ITEM_PRIORITIES.MEDIUM,
      status: actionItem.status || ACTION_ITEM_STATUSES.OPEN,
      deadlineAt: actionItem.deadlineAt || null,
    });
    setExpandedActionItems(prev => new Set(prev).add(actionItem.id));
  };

  const handleCancelEditActionItem = () => {
    setEditingActionItemId(null);
    setEditingActionItem({});
  };

  const handleSaveActionItem = async (actionItemId) => {
    if (!editingActionItem.title?.trim()) return;
    try {
      await meetingsApi.updateActionItem(meetingId, actionItemId, {
        title: editingActionItem.title.trim(),
        description: editingActionItem.description?.trim() || null,
        ownerEmployeeId: editingActionItem.ownerEmployeeId || null,
        priority: editingActionItem.priority || ACTION_ITEM_PRIORITIES.MEDIUM,
        status: editingActionItem.status,
        deadlineAt: editingActionItem.deadlineAt || null,
      });
      showAlert('Action item updated', 'success');
      setEditingActionItemId(null);
      setEditingActionItem({});
      onChange?.();
      fetchActionItems();
    } catch (err) {
      showAlert('Failed to update action item', 'error', err);
    }
  };

  const getDefaultActionItemState = () => ({
    title: '',
    description: '',
    ownerEmployeeId: null,
    ownerEmployee: null,
    priority: ACTION_ITEM_PRIORITIES.MEDIUM,
    status: ACTION_ITEM_STATUSES.OPEN,
    deadlineAt: null,
  });

  const handleAddActionItemClick = (sourceNote = null) => {
    setShowAddActionItemForm(true);
    setNewActionItem(getDefaultActionItemState());
    setActionItemSourceNote(sourceNote);
    // Close note menu if open
    setNoteMenuAnchor(null);
    setSelectedNoteForMenu(null);
  };

  const handleCancelAddActionItem = () => {
    setShowAddActionItemForm(false);
    setNewActionItem(getDefaultActionItemState());
    setActionItemSourceNote(null);
  };

  const handleAddActionItem = async () => {
    if (!newActionItem.title?.trim()) return;

    try {
      setIsAddingActionItem(true);
      const actionItemData = {
        title: newActionItem.title.trim(),
        description: newActionItem.description?.trim() || null,
        ownerEmployeeId: newActionItem.ownerEmployeeId || null,
        priority: newActionItem.priority || ACTION_ITEM_PRIORITIES.MEDIUM,
        status: newActionItem.status || ACTION_ITEM_STATUSES.OPEN,
        deadlineAt: newActionItem.deadlineAt || null,
      };

      // Link to source note if creating from a note
      if (actionItemSourceNote) {
        actionItemData.sourceMeetingNoteId = actionItemSourceNote.id;
      }

      // Link to talking point if in talking point view
      if (viewMode === VIEW_MODES.TALKING_POINT && selectedPoint) {
        actionItemData.sourceMeetingTalkingPointId = selectedPoint.id;
      }

      await meetingsApi.addActionItem(meetingId, actionItemData);
      showAlert('Action item added', 'success');
      setNewActionItem(getDefaultActionItemState());
      setShowAddActionItemForm(false);
      setActionItemSourceNote(null);
      onChange?.();
      fetchActionItems();
    } catch (err) {
      showAlert('Failed to add action item', 'error', err);
    } finally {
      setIsAddingActionItem(false);
    }
  };

  const handleDeleteActionItem = async () => {
    if (!actionItemToDelete) return;
    try {
      await meetingsApi.removeActionItem(meetingId, actionItemToDelete.id);
      showAlert('Action item removed', 'success');
      setActionItemToDelete(null);
      onChange?.();
      fetchActionItems();
    } catch (err) {
      showAlert('Failed to remove action item', 'error', err);
    }
  };

  const handleActionItemStatusChange = async (actionItem, newStatus) => {
    try {
      await meetingsApi.updateActionItem(meetingId, actionItem.id, {
        status: newStatus,
      });
      onChange?.();
      fetchActionItems();
    } catch (err) {
      showAlert('Failed to update action item status', 'error', err);
    }
  };

  // Note menu handlers
  const handleNoteMenuOpen = (event, note) => {
    event.stopPropagation();
    setNoteMenuAnchor(event.currentTarget);
    setSelectedNoteForMenu(note);
  };

  const handleNoteMenuClose = () => {
    setNoteMenuAnchor(null);
    setSelectedNoteForMenu(null);
  };

  // Meeting Notes contextual menu handlers
  const handleMeetingNotesMenuOpen = (event) => {
    setMeetingNotesMenuAnchor(event.currentTarget);
  };

  const handleMeetingNotesMenuClose = () => {
    setMeetingNotesMenuAnchor(null);
  };

  const handleLinkGoalActionSuccess = () => {
    fetchLinkedReferences();
    onChange?.();
    onRefresh?.();
  };

  const handleCreateIdpSuccess = ({ goalId, linked }) => {
    if (linked) {
      showAlert('Development Plan created and linked successfully', 'success');
      fetchLinkedReferences();
      onChange?.();
    } else {
      showAlert('Development Plan created successfully', 'success');
    }
  };

  const handleTpCreateIdpSuccess = ({ goalId, linked }) => {
    if (linked) {
      showAlert('Development Plan created and linked successfully', 'success');
      if (selectedPoint?.id) {
        fetchTalkingPointReferences(selectedPoint.id);
      }
      onChange?.();
    } else {
      showAlert('Development Plan created successfully', 'success');
    }
  };

  const handleRemoveLinkedReference = async () => {
    if (!linkedRefToDelete) return;
    try {
      await meetingsApi.removeMeetingReference(meetingId, linkedRefToDelete.id);
      showAlert('Link removed', 'success');
      onChange?.();
      fetchLinkedReferences();
    } catch (err) {
      showAlert('Failed to remove link', 'error', err);
    } finally {
      setLinkedRefToDelete(null);
    }
  };

  const handleLinkedItemClick = (ref) => {
    const refType = ref.refType || ref.referenceType;

    // Handle PERFORMANCE_FEEDBACK_RESULT
    if (refType === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT) {
      const assignmentId = ref.refEntity || ref.refEntityData?.id;
      if (assignmentId) {
        setFeedbackViewModal({ open: true, assignmentId });
      }
      return;
    }

    // For both goals and actions, we need the goal ID
    // For goals: use refEntity directly
    // For actions: use refEntityData.developmentGoal.id (parent goal)
    const goalId = refType === MEETING_REFERENCE_TYPES.IDP_GOAL
      ? (ref.refEntity || ref.refEntityData?.id)
      : ref.refEntityData?.developmentGoal?.id;

    // Open Actions tab when clicking on an action
    const initialTab = refType === MEETING_REFERENCE_TYPES.IDP_ACTION ? IDP_TABS.ACTIONS : IDP_TABS.GENERAL;

    if (goalId) {
      setIdpViewModal({ open: true, goalId, initialTab });
    }
  };

  // Get mandatory attendees for the Link Goal/Action modal
  const getMandatoryAttendees = () => {
    return (meeting?.attendees || []).filter(a =>
      a.attendeeType?.code === MEETING_ATTENDEE_TYPES.MANDATORY
    );
  };

  // Talking Point References handlers
  const fetchTalkingPointReferences = async (talkingPointId) => {
    if (!talkingPointId) {
      return;
    }
    try {
      const refs = await meetingsApi.getTalkingPointReferences(meetingId, talkingPointId);
      // Filter for IDP_GOAL, IDP_ACTION, and PERFORMANCE_FEEDBACK_RESULT types
      const filteredRefs = (refs || []).filter(r =>
        r.refType === MEETING_REFERENCE_TYPES.IDP_GOAL ||
        r.refType === MEETING_REFERENCE_TYPES.IDP_ACTION ||
        r.refType === MEETING_REFERENCE_TYPES.PERFORMANCE_FEEDBACK_RESULT
      );
      setTalkingPointReferences(prev => ({
        ...prev,
        [talkingPointId]: filteredRefs,
      }));
    } catch (err) {
      console.error('Failed to fetch talking point references:', err);
    }
  };

  const handleTalkingPointMenuOpen = (event) => {
    setTalkingPointMenuAnchor(event.currentTarget);
  };

  const handleTalkingPointMenuClose = () => {
    setTalkingPointMenuAnchor(null);
  };

  const handleTalkingPointLinkSuccess = () => {
    if (selectedPoint) {
      fetchTalkingPointReferences(selectedPoint.id);
    }
    onChange?.();
    onRefresh?.();
  };

  const handleRemoveTalkingPointReference = async () => {
    if (!tpLinkedRefToDelete || !selectedPoint) {
      return;
    }
    try {
      await meetingsApi.removeTalkingPointReference(meetingId, selectedPoint.id, tpLinkedRefToDelete.id);
      showAlert('Link removed', 'success');
      onChange?.();
      fetchTalkingPointReferences(selectedPoint.id);
    } catch (err) {
      showAlert('Failed to remove link', 'error', err);
    } finally {
      setTpLinkedRefToDelete(null);
    }
  };

  // Get references for the current talking point
  const getTalkingPointReferencesForCurrentPoint = () => {
    if (!selectedPoint) {
      return [];
    }
    return talkingPointReferences[selectedPoint.id] || [];
  };

  // Helper to get action items for current view
  const getActionItemsForCurrentView = () => {
    if (viewMode === VIEW_MODES.TALKING_POINT && selectedPoint) {
      return actionItems.filter(item => item.sourceMeetingTalkingPointId === selectedPoint.id);
    } else if (viewMode === VIEW_MODES.GENERAL_NOTES) {
      // General action items (not linked to a talking point)
      return actionItems.filter(item => !item.sourceMeetingTalkingPointId);
    }
    return [];
  };

  // Helper to get action items for a specific note
  const getActionItemsForNote = (noteId) => {
    return actionItems.filter(item => item.sourceMeetingNoteId === noteId);
  };

  const getQuestion = (point) => {
    return point.talkingPoint?.question || point.question || point.systemTalkingPoint?.question;
  };

  const getInstructions = (point) => {
    return point.talkingPoint?.instructions || point.instructions || point.systemTalkingPoint?.instructions;
  };

  const isSystem = (point) => {
    return point.talkingPoint?.isSystemTalkingPoint;
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, height: '100%', minHeight: 500 }}>
      {/* Left Panel - Talking Points List */}
      <DiscussionLeftPanel
        isLoading={isLoading}
        talkingPoints={talkingPoints}
        selectedPoint={selectedPoint}
        viewMode={viewMode}
        generalNotes={generalNotes}
        actionItems={actionItems}
        onSelectPoint={handleSelectPoint}
        onSelectGeneralNotes={handleSelectGeneralNotes}
      />

      {/* Right Panel - Details (70%) */}
      <Paper
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Empty State - no selection */}
        {!viewMode && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'text.secondary',
            }}
          >
            <FaComments style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
            <Typography variant="body1">
              Select an item to view details and notes
            </Typography>
          </Box>
        )}

        {/* Talking Point View */}
        {viewMode === VIEW_MODES.TALKING_POINT && selectedPoint && (
          <Box sx={{ p: 3, overflow: 'auto', display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <TalkingPointStatusSelector
                  status={selectedPoint.status || TALKING_POINT_STATUSES.PENDING}
                  onChange={(newStatus) => handleStatusChange(selectedPoint, newStatus)}
                  disabled={!permissions.canAddNotes}
                  size="medium"
                />
                <Typography
                  variant="h6"
                  sx={{
                    flex: 1,
                    color: '#1F2937',
                  }}
                >
                  {getQuestion(selectedPoint)}
                </Typography>
                {permissions.canAddNotes && (
                  <IconButton
                    size="small"
                    onClick={handleTalkingPointMenuOpen}
                    sx={{ color: '#6B7280' }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#9CA3AF', fontSize: '12px' }}
                >
                  {isSystem(selectedPoint) ? 'System' : 'Custom'}
                </Typography>
                {selectedPoint.addedByEmployee && (
                  <>
                    <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '12px' }}>
                      •
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '12px' }}>
                      Added by {selectedPoint.addedByEmployee.fullName}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Instructions */}
            {getInstructions(selectedPoint) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Instructions
                </Typography>
                <Typography variant="body2" sx={{ backgroundColor: '#F8FAFC', p: 2, borderRadius: 1 }}>
                  {getInstructions(selectedPoint)}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Linked Items Section */}
            <LinkedItemsSection
              linkedReferences={getTalkingPointReferencesForCurrentPoint()}
              onItemClick={handleLinkedItemClick}
              onDeleteClick={setTpLinkedRefToDelete}
              canDelete={permissions.canAddNotes}
            />

            {getTalkingPointReferencesForCurrentPoint().length > 0 && <Divider sx={{ my: 2 }} />}

            {/* Action Items Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Action Items{getActionItemsForCurrentView().length > 0 && ` (${getActionItemsForCurrentView().length})`}
              </Typography>

              {/* Action Items List */}
              <Box sx={{ mb: 2 }}>
                {getActionItemsForCurrentView().length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click any action item to view or edit details.
                  </Typography>
                )}
                {getActionItemsForCurrentView().map((actionItem) => (
                  <ActionItemCard
                    key={actionItem.id}
                    actionItem={actionItem}
                    variant="full"
                    isExpanded={expandedActionItems.has(actionItem.id)}
                    isEditing={editingActionItemId === actionItem.id}
                    editingItem={editingActionItem}
                    onToggleExpand={() => toggleActionItemExpanded(actionItem.id)}
                    onStartEdit={() => handleStartEditActionItem(actionItem)}
                    onCancelEdit={handleCancelEditActionItem}
                    onSaveEdit={() => handleSaveActionItem(actionItem.id)}
                    onDelete={() => setActionItemToDelete(actionItem)}
                    onStatusChange={(status) => handleActionItemStatusChange(actionItem, status)}
                    onEditChange={setEditingActionItem}
                    onScrollToNote={(noteId) => {
                      setExpandedNotes(prev => new Set(prev).add(noteId));
                      setTimeout(() => {
                        const noteElement = document.getElementById(`note-${noteId}`);
                        if (noteElement) {
                          noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 100);
                    }}
                    canEdit={permissions.canAddNotes}
                  />
                ))}

                {/* Add Action Item Form - only show at top level when NOT creating from a note */}
                {permissions.canAddNotes && showAddActionItemForm && !actionItemSourceNote && (
                  <ActionItemForm
                    variant="full"
                    item={newActionItem}
                    onChange={setNewActionItem}
                    onSave={handleAddActionItem}
                    onCancel={handleCancelAddActionItem}
                    isLoading={isAddingActionItem}
                    isNew={true}
                  />
                )}

                {/* Add Action Item Button - show when top-level form is not open */}
                {permissions.canAddNotes && !(showAddActionItemForm && !actionItemSourceNote) && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddActionItemClick()}
                    sx={{
                      color: '#2563EB',
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Add Action Item
                  </Button>
                )}

                {/* Empty state */}
                {getActionItemsForCurrentView().length === 0 && !(showAddActionItemForm && !actionItemSourceNote) && (
                  <EmptyState
                    title="No action items"
                    description="Add an action item to track follow-ups"
                    textSize="h8"
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Notes Section - Talking Point Notes */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Notes{selectedPoint.notes?.length > 0 && ` (${selectedPoint.notes.length})`}
              </Typography>

              {/* Notes List */}
              <Box sx={{ mb: 2 }}>
                {selectedPoint.notes?.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click any note to view or edit details.
                  </Typography>
                )}
                {selectedPoint.notes?.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    variant="full"
                    isExpanded={expandedNotes.has(note.id)}
                    isEditing={editingNoteId === note.id}
                    editingContent={editingNoteContent}
                    onToggleExpand={() => toggleNoteExpanded(note.id)}
                    onStartEdit={() => handleStartEditNote(note)}
                    onCancelEdit={handleCancelEditNote}
                    onSaveEdit={() => handleSaveNote(note.id)}
                    onMenuOpen={handleNoteMenuOpen}
                    onEditChange={setEditingNoteContent}
                    canEdit={permissions.canAddNotes}
                    actionItems={getActionItemsForNote(note.id)}
                    actionItemsProps={{
                      expandedActionItems,
                      editingActionItemId,
                      editingItem: editingActionItem,
                      onToggleExpand: toggleActionItemExpanded,
                      onStartEdit: handleStartEditActionItem,
                      onCancelEdit: handleCancelEditActionItem,
                      onSaveEdit: handleSaveActionItem,
                      onDelete: setActionItemToDelete,
                      onStatusChange: handleActionItemStatusChange,
                      onEditChange: setEditingActionItem,
                      canEdit: permissions.canAddNotes,
                    }}
                    showAddActionItemForm={showAddActionItemForm && actionItemSourceNote?.id === note.id}
                    newActionItem={newActionItem}
                    onNewActionItemChange={setNewActionItem}
                    onAddActionItem={handleAddActionItem}
                    onCancelAddActionItem={handleCancelAddActionItem}
                    onAddActionItemClick={handleAddActionItemClick}
                    isAddingActionItem={isAddingActionItem}
                  />
                ))}

                {/* Add Note Form */}
                {permissions.canAddNotes && (
                  showAddNoteForm ? (
                    <NoteForm
                      variant="full"
                      content={newNoteContent}
                      onChange={setNewNoteContent}
                      onSave={handleAddNote}
                      onCancel={handleCancelAddNote}
                      isLoading={isAddingNote}
                      isNew={true}
                    />
                  ) : (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={handleAddNoteClick}
                      sx={{
                        color: '#2563EB',
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Add Note
                    </Button>
                  )
                )}

                {/* Empty state when no notes and not adding */}
                {(!selectedPoint.notes || selectedPoint.notes.length === 0) && !showAddNoteForm && (
                  <EmptyState
                    title="No notes added yet"
                    description="Add a note to record discussion points"
                    textSize="h8"
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* General Notes View */}
        {viewMode === VIEW_MODES.GENERAL_NOTES && (
          <Box sx={{ p: 3, overflow: 'auto', display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 24, color: '#6B7280' }} />
                <Typography variant="h6" sx={{ color: '#1F2937', flex: 1 }}>
                  Meeting Notes
                </Typography>
                {permissions.canAddNotes && (
                  <IconButton
                    size="small"
                    onClick={handleMeetingNotesMenuOpen}
                    sx={{ color: '#6B7280' }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                General notes for this meeting, not attached to specific talking points
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Linked Items Section */}
            <LinkedItemsSection
              linkedReferences={linkedReferences}
              onItemClick={handleLinkedItemClick}
              onDeleteClick={setLinkedRefToDelete}
              canDelete={permissions.canAddNotes}
            />

            {linkedReferences.length > 0 && <Divider sx={{ my: 2 }} />}

            {/* Action Items Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Action Items{getActionItemsForCurrentView().length > 0 && ` (${getActionItemsForCurrentView().length})`}
              </Typography>

              {/* Action Items List */}
              <Box sx={{ mb: 2 }}>
                {getActionItemsForCurrentView().length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click any action item to view or edit details.
                  </Typography>
                )}
                {getActionItemsForCurrentView().map((actionItem) => (
                  <ActionItemCard
                    key={actionItem.id}
                    actionItem={actionItem}
                    variant="full"
                    isExpanded={expandedActionItems.has(actionItem.id)}
                    isEditing={editingActionItemId === actionItem.id}
                    editingItem={editingActionItem}
                    onToggleExpand={() => toggleActionItemExpanded(actionItem.id)}
                    onStartEdit={() => handleStartEditActionItem(actionItem)}
                    onCancelEdit={handleCancelEditActionItem}
                    onSaveEdit={() => handleSaveActionItem(actionItem.id)}
                    onDelete={() => setActionItemToDelete(actionItem)}
                    onStatusChange={(status) => handleActionItemStatusChange(actionItem, status)}
                    onEditChange={setEditingActionItem}
                    onScrollToNote={(noteId) => {
                      setExpandedNotes(prev => new Set(prev).add(noteId));
                      setTimeout(() => {
                        const noteElement = document.getElementById(`note-${noteId}`);
                        if (noteElement) {
                          noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 100);
                    }}
                    canEdit={permissions.canAddNotes}
                  />
                ))}

                {/* Add Action Item Form - only show at top level when NOT creating from a note */}
                {permissions.canAddNotes && showAddActionItemForm && !actionItemSourceNote && (
                  <ActionItemForm
                    variant="full"
                    item={newActionItem}
                    onChange={setNewActionItem}
                    onSave={handleAddActionItem}
                    onCancel={handleCancelAddActionItem}
                    isLoading={isAddingActionItem}
                    isNew={true}
                  />
                )}

                {/* Add Action Item Button - show when top-level form is not open */}
                {permissions.canAddNotes && !(showAddActionItemForm && !actionItemSourceNote) && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddActionItemClick()}
                    sx={{
                      color: '#2563EB',
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Add Action Item
                  </Button>
                )}

                {/* Empty state */}
                {getActionItemsForCurrentView().length === 0 && !(showAddActionItemForm && !actionItemSourceNote) && (
                  <EmptyState
                    title="No action items"
                    description="Add an action item to track follow-ups"
                    textSize="h8"
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Notes Section - General Meeting Notes */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Notes{generalNotes.length > 0 && ` (${generalNotes.length})`}
              </Typography>

              {/* Notes List */}
              <Box sx={{ mb: 2 }}>
                {generalNotes.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click any note to view or edit details.
                  </Typography>
                )}
                {generalNotes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    variant="full"
                    isExpanded={expandedNotes.has(note.id)}
                    isEditing={editingNoteId === note.id}
                    editingContent={editingNoteContent}
                    onToggleExpand={() => toggleNoteExpanded(note.id)}
                    onStartEdit={() => handleStartEditNote(note)}
                    onCancelEdit={handleCancelEditNote}
                    onSaveEdit={() => handleSaveNote(note.id)}
                    onMenuOpen={handleNoteMenuOpen}
                    onEditChange={setEditingNoteContent}
                    canEdit={permissions.canAddNotes}
                    actionItems={getActionItemsForNote(note.id)}
                    actionItemsProps={{
                      expandedActionItems,
                      editingActionItemId,
                      editingItem: editingActionItem,
                      onToggleExpand: toggleActionItemExpanded,
                      onStartEdit: handleStartEditActionItem,
                      onCancelEdit: handleCancelEditActionItem,
                      onSaveEdit: handleSaveActionItem,
                      onDelete: setActionItemToDelete,
                      onStatusChange: handleActionItemStatusChange,
                      onEditChange: setEditingActionItem,
                      canEdit: permissions.canAddNotes,
                    }}
                    showAddActionItemForm={showAddActionItemForm && actionItemSourceNote?.id === note.id}
                    newActionItem={newActionItem}
                    onNewActionItemChange={setNewActionItem}
                    onAddActionItem={handleAddActionItem}
                    onCancelAddActionItem={handleCancelAddActionItem}
                    onAddActionItemClick={handleAddActionItemClick}
                    isAddingActionItem={isAddingActionItem}
                  />
                ))}

                {/* Add Note Form */}
                {permissions.canAddNotes && (
                  showAddNoteForm ? (
                    <NoteForm
                      variant="full"
                      content={newNoteContent}
                      onChange={setNewNoteContent}
                      onSave={handleAddGeneralNote}
                      onCancel={handleCancelAddNote}
                      isLoading={isAddingNote}
                      isNew={true}
                    />
                  ) : (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={handleAddNoteClick}
                      sx={{
                        color: '#2563EB',
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Add Note
                    </Button>
                  )
                )}

                {/* Empty state when no notes and not adding */}
                {generalNotes.length === 0 && !showAddNoteForm && (
                  <EmptyState
                    title="No notes added yet"
                    description="Add a note to record general meeting information"
                    textSize="h8"
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Note Context Menu */}
      <Menu
        anchorEl={noteMenuAnchor}
        open={Boolean(noteMenuAnchor)}
        onClose={handleNoteMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => {
          handleNoteMenuClose();
          handleAddActionItemClick(selectedNoteForMenu);
        }}>
          <AddIcon sx={{ fontSize: 18, mr: 1, color: '#2563EB' }} />
          Add Action Item
        </MenuItem>
        <MenuItem onClick={() => {
          handleNoteMenuClose();
          if (selectedNoteForMenu) setNoteToDelete(selectedNoteForMenu);
        }}>
          <DeleteIcon sx={{ fontSize: 18, mr: 1, color: '#dc2626' }} />
          Delete Note
        </MenuItem>
      </Menu>

      {/* Meeting Notes Context Menu */}
      <LinkItemsMenu
        anchorEl={meetingNotesMenuAnchor}
        onClose={handleMeetingNotesMenuClose}
        onLinkGoalAction={() => setLinkGoalModalOpen(true)}
        onLinkFeedbackResults={() => setLinkFeedbackModalOpen(true)}
        onCreateDevPlan={() => setCreateIdpModalOpen(true)}
      />

      {/* Talking Point Context Menu */}
      <LinkItemsMenu
        anchorEl={talkingPointMenuAnchor}
        onClose={handleTalkingPointMenuClose}
        onLinkGoalAction={() => setTalkingPointLinkGoalModalOpen(true)}
        onLinkFeedbackResults={() => setTalkingPointLinkFeedbackModalOpen(true)}
        onCreateDevPlan={() => setTpCreateIdpModalOpen(true)}
      />

      {/* Link Goal/Action Modal (Meeting level) */}
      <LinkGoalActionModal
        open={linkGoalModalOpen}
        onClose={() => setLinkGoalModalOpen(false)}
        meetingId={meetingId}
        mandatoryAttendees={getMandatoryAttendees()}
        onSuccess={handleLinkGoalActionSuccess}
      />

      {/* Link Goal/Action Modal (Talking Point level) */}
      <LinkGoalActionModal
        open={talkingPointLinkGoalModalOpen}
        onClose={() => setTalkingPointLinkGoalModalOpen(false)}
        meetingId={meetingId}
        talkingPointId={selectedPoint?.id}
        mandatoryAttendees={getMandatoryAttendees()}
        onSuccess={handleTalkingPointLinkSuccess}
      />

      <ConfirmationModal
        isOpen={!!noteToDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        actionButtonText="Delete"
        actionButtonColor="#dc2626"
        onConfirm={handleDeleteNote}
        onClose={() => setNoteToDelete(null)}
      />

      <ConfirmationModal
        isOpen={!!actionItemToDelete}
        title="Delete Action Item"
        message="Are you sure you want to delete this action item? This action cannot be undone."
        actionButtonText="Delete"
        actionButtonColor="#dc2626"
        onConfirm={handleDeleteActionItem}
        onClose={() => setActionItemToDelete(null)}
      />

      <ConfirmationModal
        isOpen={!!linkedRefToDelete}
        title="Remove Link"
        message={`Are you sure you want to remove the link to "${linkedRefToDelete?.refEntityData?.title || linkedRefToDelete?.refEntityData?.name || 'this item'}"?`}
        actionButtonText="Remove"
        actionButtonColor="#dc2626"
        onConfirm={handleRemoveLinkedReference}
        onClose={() => setLinkedRefToDelete(null)}
      />

      {/* IDP View Modal */}
      <IDPViewModal
        open={idpViewModal.open}
        onClose={() => setIdpViewModal({ open: false, goalId: null, initialTab: IDP_TABS.GENERAL })}
        onSave={() => {
          if (selectedPoint?.id) {
            fetchTalkingPointReferences(selectedPoint.id);
          } else {
            fetchLinkedReferences();
          }
        }}
        goalId={idpViewModal.goalId}
        initialTab={idpViewModal.initialTab}
      />

      {/* Link Feedback Results Modal (Meeting level) */}
      <LinkFeedbackResultsModal
        open={linkFeedbackModalOpen}
        onClose={() => setLinkFeedbackModalOpen(false)}
        meetingId={meetingId}
        mandatoryAttendees={getMandatoryAttendees()}
        onSuccess={handleLinkGoalActionSuccess}
      />

      {/* Link Feedback Results Modal (Talking Point level) */}
      <LinkFeedbackResultsModal
        open={talkingPointLinkFeedbackModalOpen}
        onClose={() => setTalkingPointLinkFeedbackModalOpen(false)}
        meetingId={meetingId}
        talkingPointId={selectedPoint?.id}
        mandatoryAttendees={getMandatoryAttendees()}
        onSuccess={handleTalkingPointLinkSuccess}
      />

      {/* Confirmation Modal for Talking Point Reference Deletion */}
      <ConfirmationModal
        isOpen={!!tpLinkedRefToDelete}
        title="Remove Link"
        message={`Are you sure you want to remove the link to "${tpLinkedRefToDelete?.refEntityData?.title || tpLinkedRefToDelete?.refEntityData?.name || 'this item'}"?`}
        actionButtonText="Remove"
        actionButtonColor="#dc2626"
        onConfirm={handleRemoveTalkingPointReference}
        onClose={() => setTpLinkedRefToDelete(null)}
      />

      {/* Feedback View Modal */}
      <FeedbackViewModal
        open={feedbackViewModal.open}
        onClose={() => setFeedbackViewModal({ open: false, assignmentId: null })}
        assignmentId={feedbackViewModal.assignmentId}
      />

      {/* Create IDP Modal (Meeting level) */}
      <CreateIDPModal
        open={createIdpModalOpen}
        onClose={() => setCreateIdpModalOpen(false)}
        mandatoryAttendees={getMandatoryAttendees()}
        meetingId={meetingId}
        onSuccess={handleCreateIdpSuccess}
      />

      {/* Create IDP Modal (Talking Point level) */}
      <CreateIDPModal
        open={tpCreateIdpModalOpen}
        onClose={() => setTpCreateIdpModalOpen(false)}
        mandatoryAttendees={getMandatoryAttendees()}
        meetingId={meetingId}
        talkingPointId={selectedPoint?.id}
        onSuccess={handleTpCreateIdpSuccess}
      />
    </Box>
  );
};

export default DiscussionTab;
